import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plug, Clapperboard, Palette, Plus, Trash2, Zap, Save } from 'lucide-react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';
import { useLabOS } from '@/lib/labos';
import GenreCatalog from '@/pages/GenreCatalog';
import StyleCatalog from '@/pages/StyleCatalog';

interface McpServer {
  id: string;
  name: string;
  description: string;
  url: string;
  auth_type: string;
  auth_secret: string;
  auth_token: string;
  enabled: boolean;
  attachments: string[];
  workflows?: Record<string, string>;
}

/** Capability keys the engine understands; labels live in the `admin` catalogue. */
const CAP_KEYS = ['nle_conform', 'asset_sync', 'script_source', 'agent_toolbox', 'generation'] as const;

// Vendor/model-agnostic capability roles a 'generation' server maps to WR workflow
// names. Story Engine only knows the capability; the workflow binds the vendor/model.
const WORKFLOW_ROLES = ['image', 'image_fallback', 'video', 'video_fallback'] as const;

const EMPTY: Omit<McpServer, 'id'> = {
  name: '',
  description: '',
  url: '',
  auth_type: 'none',
  auth_secret: '',
  auth_token: '',
  enabled: true,
  attachments: [],
  workflows: {},
};

function McpManager() {
  const { t } = useTranslation(['admin', 'common']);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [servers, setServers] = useState<McpServer[]>([]);
  const [caps, setCaps] = useState<string[]>([...CAP_KEYS]);
  const [editing, setEditing] = useState<McpServer | (typeof EMPTY & { id?: string }) | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await apiFetch('/api/mcp/servers');
    if (r.ok) {
      const d = await r.json();
      setServers(d.servers || []);
      if (d.capabilities?.length) setCaps(d.capabilities);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    const isNew = !('id' in editing) || !editing.id;
    const path = isNew ? '/api/mcp/servers' : `/api/mcp/servers/${editing.id}`;
    await apiFetch(path, {
      method: isNew ? 'POST' : 'PATCH',
      body: JSON.stringify(editing),
    });
    setBusy(false);
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    await apiFetch(`/api/mcp/servers/${id}`, { method: 'DELETE' });
    load();
  };

  const test = async (id: string) => {
    setTestResult((t) => ({ ...t, [id]: 'testing…' }));
    const r = await apiFetch(`/api/mcp/servers/${id}/test`, { method: 'POST' });
    const d = await r.json();
    setTestResult((t) => ({
      ...t,
      [id]: d.ok
        ? `OK — ${(d.tools || []).length} tools: ${(d.tools || [])
            .map((x: { name: string }) => x.name)
            .slice(0, 6)
            .join(', ')}`
        : `FAILED — ${d.error}`,
    }));
  };

  const toggleCap = (cap: string) => {
    if (!editing) return;
    const has = editing.attachments.includes(cap);
    setEditing({
      ...editing,
      attachments: has ? editing.attachments.filter((c) => c !== cap) : [...editing.attachments, cap],
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('mcp.intro')}</p>
        <Button size="sm" onClick={() => setEditing({ ...EMPTY })}>
          <Plus className="mr-1 h-4 w-4" /> {t('mcp.addServer')}
        </Button>
      </div>

      {editing && (
        <Card className="border-primary">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs">
                Name
                <input
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Gateway URL
                <input
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-background"
                  placeholder="https://…/mcp/"
                  value={editing.url}
                  onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                />
              </label>
            </div>
            <label className="text-xs block">
              Description
              <input
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-background"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs">
                Auth
                <select
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={editing.auth_type}
                  onChange={(e) => setEditing({ ...editing, auth_type: e.target.value })}
                >
                  <option value="none">None</option>
                  <option value="api_key">X-API-Key</option>
                  <option value="bearer">Bearer token</option>
                </select>
              </label>
              <label className="text-xs">
                Secret Manager name (preferred)
                <input
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={editing.auth_secret}
                  onChange={(e) => setEditing({ ...editing, auth_secret: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Inline token
                <input
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-background"
                  type="password"
                  value={editing.auth_token}
                  onChange={(e) => setEditing({ ...editing, auth_token: e.target.value })}
                />
              </label>
            </div>
            <div className="text-xs">
              <span className="text-muted-foreground">Attachments:</span>
              <div className="flex gap-2 mt-1 flex-wrap">
                {caps.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCap(c)}
                    className={`px-2 py-1 rounded border text-xs ${
                      editing.attachments.includes(c) ? 'bg-primary text-primary-foreground' : 'bg-background'
                    }`}
                  >
                    {t(`cap.${c}`, { defaultValue: c })}
                  </button>
                ))}
              </div>
            </div>
            {editing.attachments.includes('generation') && (
              <div className="text-xs rounded border border-primary/30 bg-primary/5 p-2">
                <div className="mb-1 flex items-center gap-1.5 text-muted-foreground">
                  {t('mcp.workflowsTitle')}
                  <HelpDot>{t('mcp.workflowsHint')}</HelpDot>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {WORKFLOW_ROLES.map((role) => (
                    <label key={role} className="block">
                      <span className="text-muted-foreground">
                        {t(`role.${role}`)} <span className="opacity-60">· {t(`role.hint.${role}`)}</span>
                      </span>
                      <input
                        className="mt-0.5 w-full border rounded px-2 py-1 bg-background"
                        value={editing.workflows?.[role] || ''}
                        placeholder={t('mcp.workflowPlaceholder')}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            workflows: { ...(editing.workflows || {}), [role]: e.target.value },
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={editing.enabled}
                onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
              />
              Enabled
            </label>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={busy || !editing.name || !editing.url}>
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {servers.map((s) => (
        <Card key={s.id} className={s.enabled ? '' : 'opacity-60'}>
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Plug className="h-4 w-4 shrink-0" />
                  <span className="font-medium">{s.name}</span>
                  {!s.enabled && <Badge variant="outline">{t('mcp.disabled')}</Badge>}
                  {(s.attachments || []).map((a) => (
                    <Badge key={a} variant="secondary">
                      {t(`cap.${a}`, { defaultValue: a })}
                    </Badge>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">{s.url}</div>
                {s.description && <div className="text-xs text-muted-foreground mt-0.5">{s.description}</div>}
                {testResult[s.id] && (
                  <div className={`text-xs mt-1 ${testResult[s.id].startsWith('OK') ? 'text-green-600' : 'text-red-500'}`}>
                    {testResult[s.id]}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => test(s.id)}>
                  <Zap className="h-4 w-4 mr-1" /> Test
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(s)}>
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPendingDelete(s.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {servers.length === 0 && !editing && <p className="text-sm text-muted-foreground">{t('mcp.empty')}</p>}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={t('mcp.deleteTitle')}
        warning={t('mcp.deleteWarning')}
        destructive
        confirmLabel={t('mcp.deleteConfirm')}
        onConfirm={() => pendingDelete && remove(pendingDelete)}
      />
    </div>
  );
}

export default function AdminPanel() {
  const { t } = useTranslation(['admin', 'catalog', 'common']);
  const { user, loading } = useLabOS();
  const [tab, setTab] = useState<'mcp' | 'genres' | 'styles'>('mcp');

  if (!loading && user && !user.isAdmin) {
    return <div className="mx-auto max-w-5xl p-8 text-center text-muted-foreground">{t('accessRequired')}</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-8 py-7">
      <h1 className="mb-4 font-display text-[22px] font-semibold">{t('title')}</h1>
      <div className="mb-5 flex flex-wrap gap-2">
        <Button variant={tab === 'mcp' ? 'default' : 'outline'} size="sm" onClick={() => setTab('mcp')}>
          <Plug className="mr-1 h-4 w-4" /> {t('tab.mcp')}
        </Button>
        <Button variant={tab === 'genres' ? 'default' : 'outline'} size="sm" onClick={() => setTab('genres')}>
          <Clapperboard className="mr-1 h-4 w-4" /> {t('genre.title', { ns: 'catalog' })}
        </Button>
        <Button variant={tab === 'styles' ? 'default' : 'outline'} size="sm" onClick={() => setTab('styles')}>
          <Palette className="mr-1 h-4 w-4" /> {t('style.title', { ns: 'catalog' })}
        </Button>
      </div>
      {tab === 'mcp' && <McpManager />}
      {tab === 'genres' && <GenreCatalog />}
      {tab === 'styles' && <StyleCatalog />}
    </div>
  );
}
